import { PublicAttendee } from '@hubs101/booking-api-skd-client/lib/types';
import {
  getPublicAgenda,
  getPublicAttendees,
  getPublicCoLocatedEventsByEventId,
  getPublicEventDetailsById,
  getPublicEventDetailsBySlug,
  getPublicSpeakerGroupsByCategory,
  getPublicEventExhibitions,
  getPublicEventPageDetailsBySlug,
  getPublicGroupDetailsById,
  getPublicPortfolioEvents,
  getPublicPortfoliosByAccount,
  getPublicExhibitionGroupsByCategory,
  getPublicBlogsByAccount,
  getPublicEvents,
} from '@hubs101/booking-api-skd-client';
import { useQueries, useQuery } from '@tanstack/react-query';

import { BaseAPI } from '@hubs101/js-api-skd-client';
import { FeedType } from '@hubs101/js-api-skd-client/lib/api/authentication/types';
import { GroupServerResponse } from '@hubs101/js-api-skd-client/lib/api/event/types';

export const ROOT_URL = process.env.ROOT_URL as string;
export const ROOT_BOOKING_URL = process.env.ROOT_BOOKING_URL as string;
export const BASE_MEDIA_URL = process.env.BASE_MEDIA_URL as string;
export const ACCOUNT_ID = process.env.ACCOUNT_ID as string;
export const PORTFOLIO_GENERAL_EMAILS = process.env
  .PORTFOLIO_GENERAL_EMAILS as string;
export const SENTRY_KEY = process.env.SENTRY_KEY as string;
export const GA_MEASUREMENT_ID = process.env.GA_MEASUREMENT_ID;
export const PASSWORD = process.env.PASSWORD as string;
export const API_CONTACT_SECRET = process.env.API_CONTACT_SECRET as string;
export const API_INBOUND_SECRET = process.env.API_INBOUND_SECRET as string;
export const WE_WEBSITE_RE_CAPTCHA_SITE_KEY = process.env
  .RECAPTCHA_KEY as string;
export const APP_HOST = process.env.APP_HOST as string;

export const usePublicAttendees = ({
  eventId,
  enabled,
  colocated = false,
  params,
}: {
  eventId: string;
  enabled: boolean;
  colocated?: boolean;
  params?: string;
}) => {
  return useQuery({
    queryKey: [
      ROOT_URL,
      'attendees',
      eventId,
      colocated ? 'colocated' : 'single',
      params ?? '',
    ],
    queryFn: async () => {
      try {
        if (!colocated) {
          const response = await getPublicAttendees({
            baseUrl: ROOT_URL ?? '',
            eventId,
            params,
          });

          return response ?? { attendees: [] };
        } else {
          const response: any = await BaseAPI.fetchColocatedAttendees(
            ROOT_URL,
            eventId
          );
          const data: {
            attendees: PublicAttendee[];
          } = {
            attendees: response || [],
          };

          return data;
        }
      } catch (e) {
        console.log('error = ', e);
        return { attendees: [] };
      }
    },
    enabled,
    staleTime: 180000,
    retry: false,
    networkMode: 'offlineFirst',
  });
};

export const usePublicPortfolioByAccount = ({
  accountId,
  page = 1,
  slug,
}: {
  accountId: string;
  page?: number;
  slug?: string;
}) => {
  return useQuery({
    queryKey: [ROOT_URL, accountId, slug, page],
    queryFn: async () =>
      await getPublicPortfoliosByAccount({
        baseUrl: ROOT_URL ?? '',
        accountId,
        page,
        slug,
      }),
    staleTime: 180000,
    retry: false,
    networkMode: 'offlineFirst',
  });
};

export const usePublicBlogsByAccount = ({
  accountId,
  page = 1,
  slug,
  tags,
  search,
}: {
  accountId: string;
  page?: number;
  slug?: string;
  search?: string;
  tags?: string;
}) => {
  return useQuery({
    queryKey: [ROOT_URL, accountId, slug, page, search, tags],
    queryFn: async () =>
      await getPublicBlogsByAccount({
        baseUrl: ROOT_URL ?? '',
        accountId,
        page,
        slug,
        search,
        tags,
      }),
    staleTime: 180000,
    retry: false,
    networkMode: 'offlineFirst',
  });
};

export const usePublicFeeds = ({
  accountId,
  blogId,
  enabled = true,
}: {
  accountId: string;
  blogId?: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [ROOT_URL, accountId, 'feeds', blogId ?? 'all'],
    queryFn: async (): Promise<FeedType[]> => {
      try {
        const response = await BaseAPI.fetchPublicFeeds(
          ROOT_URL,
          accountId,
          blogId ? `blog_id=${blogId}` : ''
        );
        const feeds = (response.data ?? []) as Array<
          FeedType & { created_at?: string }
        >;

        return [...feeds].sort((firstFeed, secondFeed) => {
          const firstDate = new Date(firstFeed?.created_at ?? '').getTime();
          const secondDate = new Date(secondFeed?.created_at ?? '').getTime();

          return secondDate - firstDate;
        });
      } catch {
        return [];
      }
    },
    enabled,
    staleTime: 180000,
    retry: false,
    networkMode: 'offlineFirst',
  });
};

export const usePublicEventDetails = ({ eventId }: { eventId: string }) => {
  return useQuery({
    queryKey: [ROOT_URL, eventId],
    queryFn: async () =>
      await getPublicEventDetailsById({
        baseUrl: ROOT_URL ?? '',
        eventId,
      }),
    staleTime: 180000,
    retry: false,
    networkMode: 'offlineFirst',
  });
};

export const usePublicEventDetailsBySlug = ({
  eventSlug,
  enabled,
}: {
  eventSlug: string;
  enabled: boolean;
}) => {
  return useQuery({
    queryKey: [ROOT_URL, eventSlug],
    queryFn: async () =>
      await getPublicEventDetailsBySlug({
        baseUrl: ROOT_URL ?? '',
        eventSlug,
      }),
    enabled,
    staleTime: 180000,
    retry: false,
    networkMode: 'offlineFirst',
  });
};

export const usePublicPortfolioEvents = ({
  accountId,
  portfolioSlug,
  params,
  enabled,
}: {
  accountId: string;
  portfolioSlug: string;
  params: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [ROOT_URL, accountId, portfolioSlug, params],
    queryFn: async () =>
      await getPublicPortfolioEvents({
        baseUrl: ROOT_URL ?? '',
        accountId,
        portfolioSlug,
        params: params ?? '',
      }),
    enabled,
    staleTime: 180000,
    retry: false,
    networkMode: 'offlineFirst',
  });
};

export const usePublicEventPageDetailsBySlug = ({
  eventId,
  pageSlug,
  enabled,
}: {
  eventId: string;
  pageSlug: string;
  enabled: boolean;
}) => {
  return useQuery({
    queryKey: [ROOT_URL, pageSlug, eventId],
    queryFn: async () =>
      await getPublicEventPageDetailsBySlug({
        baseUrl: ROOT_URL ?? '',
        eventId,
        pageSlug,
      }),
    enabled,
    staleTime: 180000,
    retry: false,
    networkMode: 'offlineFirst',
  });
};

export const usePublicGroupDetailsById = ({
  groupId,
  enabled,
}: {
  groupId: string;
  enabled: boolean;
}) => {
  return useQuery({
    queryKey: [ROOT_URL, groupId],
    queryFn: async () =>
      await getPublicGroupDetailsById({
        baseUrl: ROOT_URL ?? '',
        groupId,
      }),
    enabled,
    staleTime: 180000,
    retry: false,
    networkMode: 'offlineFirst',
  });
};

export const usePublicCoLocatedEventsByEventId = ({
  eventId,
  enabled,
}: {
  eventId: string;
  enabled: boolean;
}) => {
  return useQuery({
    queryKey: [ROOT_URL, eventId],
    queryFn: async () =>
      await getPublicCoLocatedEventsByEventId({
        baseUrl: ROOT_URL ?? '',
        eventId,
      }),
    enabled,
    staleTime: 180000,
    retry: false,
    networkMode: 'offlineFirst',
  });
};

export const usePublicAgendas = ({
  page = 1,
  language = 'en',
  eventIds,
  enabled,
}: {
  page?: number;
  language?: string;
  eventIds: string[];
  enabled: boolean;
}) => {
  return useQueries({
    queries: eventIds?.map((event: string) => {
      return {
        queryKey: [ROOT_URL, page, language, event, 'agenda'],
        queryFn: async () => {
          return await getPublicAgenda({
            baseUrl: ROOT_URL ?? '',
            page,
            eventId: event,
            language,
          });
        },
        enabled,
        staleTime: 180000,
        retry: false,
      };
    }),
  });
};

export const usePublicAllAgendas = ({
  page = 1,
  language = 'en',
  eventId,
  enabled,
  colocated = false,
}: {
  page?: number;
  language?: string;
  eventId: string;
  enabled: boolean;
  colocated: boolean;
}) => {
  return useQuery({
    queryKey: [ROOT_URL, language, eventId, enabled, 'agenda'],
    queryFn: async () => {
      try {
        if (!colocated) {
          const response = await getPublicAgenda({
            baseUrl: ROOT_URL ?? '',
            page,
            eventId,
            language,
          });

          return response;
        } else {
          const response: any = await BaseAPI.fetchColocatedAgenda(
            ROOT_URL,
            eventId
          );
          const data = [];
          data.push(response);

          return data;
        }
      } catch (e) {
        console.log('error = ', e);
      }
    },
    enabled,
    staleTime: 180000,
    retry: false,
  });
};

export const usePublicExhibitions = ({
  page = 1,
  language = 'en',
  eventIds,
  enabled,
}: {
  page?: number;
  language?: string;
  eventIds: string[];
  enabled: boolean;
}) => {
  const validEventIds = eventIds?.filter(Boolean);

  const queries =
    enabled && validEventIds?.length
      ? validEventIds.map((eventId: string) => ({
          queryKey: [ROOT_URL, page, language, eventId, 'exhibitions'],
          queryFn: async () => {
            return await getPublicEventExhibitions({
              baseUrl: ROOT_URL ?? '',
              page,
              eventId,
              language,
            });
          },
          staleTime: 180000,
          retry: false,
          enabled: true, // safe to enable since we're already checking above
        }))
      : [];

  return useQueries({ queries });
};

export const useGetPublicSpeakerGroupsByCategory = ({
  eventId,
  category,
  enabled = true,
}: {
  eventId: string;
  category: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [ROOT_URL, eventId, category, 'speaker-groups'],
    queryFn: async () => {
      return await getPublicSpeakerGroupsByCategory({
        baseUrl: ROOT_URL,
        eventId,
        category,
      });
    },
    staleTime: 180000,
    enabled: enabled && !!eventId,
    retry: false,
    networkMode: 'offlineFirst',
  });
};

export const useGetPublicExhibitionGroupsByCategory = ({
  eventId,
  category,
  enabled = true,
}: {
  eventId: string;
  category: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [ROOT_URL, eventId, category, 'groups'],
    queryFn: async () => {
      return await getPublicExhibitionGroupsByCategory({
        baseUrl: ROOT_URL,
        eventId,
        category,
      });
    },
    staleTime: 180000,
    enabled,
    retry: false,
    networkMode: 'offlineFirst',
  });
};

export const useGetMergedGroupsData = ({
  eventIds,
  categories, // the order of the items (categories) matters
}: {
  eventIds: string[];
  categories: string[];
}) => {
  return useQuery({
    queryKey: [ROOT_URL, eventIds, categories, 'groups'],
    queryFn: async () => {
      const totalGroupData = await Promise.all(
        eventIds.map(async eventId => {
          let groupsData: GroupServerResponse[] = [];

          try {
            const data = await BaseAPI.fetchPublicEventGroups(
              ROOT_URL,
              eventId
            );
            groupsData = [...data];
          } catch (error) {
            throw new Error(`Failed to fetch event groups (id: ${eventId})`);
          }

          const relevantGroups: GroupServerResponse[] =
            groupsData?.filter(
              (group: GroupServerResponse) =>
                group.category && categories.includes(group.category)
            ) || [];

          return relevantGroups;
        })
      );
      const mergedGroupData = totalGroupData
        .flat()
        ?.sort(
          (a: GroupServerResponse, b: GroupServerResponse) =>
            categories.indexOf(a?.category || '') -
            categories.indexOf(b?.category || '')
        );

      const totalExhibitionGroupsData = await Promise.all(
        mergedGroupData.map(async item => {
          const data = await getPublicExhibitionGroupsByCategory({
            baseUrl: ROOT_URL,
            eventId: item.event,
            category: item.category || '',
          });

          return data.map(element => {
            return {
              ...element,
              category: item.category,
              groupName: item.name,
            };
          });
        })
      );

      const exhibitionGroups = totalExhibitionGroupsData.flat();

      const sortedExhibitionGroups = exhibitionGroups.sort((a, b) => {
        const categoryOrderA = categories.indexOf(a?.category || '');
        const categoryOrderB = categories.indexOf(b?.category || '');

        if (categoryOrderA !== categoryOrderB) {
          return categoryOrderA - categoryOrderB;
        }

        // Then sort by order field within the same category
        const orderA = a?.order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b?.order ?? Number.MAX_SAFE_INTEGER;

        return orderA - orderB;
      });

      return sortedExhibitionGroups;
    },
    staleTime: 180000,
    retry: false,
    networkMode: 'offlineFirst',
  });
};

export const useGetPublicEventAdvertisement = ({
  eventId,
}: {
  eventId: string;
}) => {
  return useQuery({
    queryKey: [ROOT_URL, eventId, 'groups'],
    queryFn: async () => {
      const data: any = await BaseAPI.fetchPublicEventAdvertisement(
        ROOT_URL,
        eventId
      );

      return [...data];
    },
    staleTime: 180000,
    enabled: !!eventId,
    retry: false,
    networkMode: 'offlineFirst',
  });
};

export const useGetPublicEventStatistics = ({
  eventId,
  enabled = true,
}: {
  eventId: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [ROOT_URL, eventId, 'statistics'],
    queryFn: async () => {
      const data = await BaseAPI.fetchPublicEventStatistics(ROOT_URL, eventId);
      return data;
    },
    enabled,
    staleTime: 180000,
    retry: false,
    networkMode: 'offlineFirst',
  });
};

export const useGetPublicEvents = ({
  language = 'en',
  limit = 100,
}: {
  language?: string;
  limit?: number;
}) => {
  return useQuery({
    queryKey: [ROOT_URL, language, limit, 'events'],
    queryFn: async () => {
      const data = await getPublicEvents({
        baseUrl: ROOT_URL,
        language: language,
        limit: limit,
      });
      return data;
    },
    enabled: true,
    staleTime: 180000,
    retry: false,
    networkMode: 'offlineFirst',
  });
};

export const useGetPublicSpeakerDetails = ({
  speakerId,
  eventId,
  enabled = true,
}: {
  speakerId: string;
  eventId: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [ROOT_URL, eventId, speakerId, 'speaker'],
    queryFn: async () => {
      const data = await BaseAPI.fetchSpeakerById(ROOT_URL, eventId, speakerId);
      return data;
    },
    enabled,
    staleTime: 180000,
    retry: false,
    networkMode: 'offlineFirst',
  });
};
